import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Mic, MicOff, X, Activity, Zap } from "lucide-react";

export const ArchitectVoiceModal: React.FC<{ onClose: () => void; initialConcierge?: boolean }> = ({
  onClose,
  initialConcierge = false,
}) => {
  const [mounted, setMounted] = useState(false);
  const [transcript, setTranscript] = useState("");
  const transcriptRef = useRef("");
  const shouldBeListeningRef = useRef(false);
  const [isListening, setIsListening] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(false);
  const [response, setResponse] = useState("");
  const [textInput, setTextInput] = useState("");

  const handleTextInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (textInput.trim().length === 0) return;
    const msg = textInput.trim();
    setTextInput("");
    if (isListening) {
      safeStopRecognition();
    }
    handleUserMessage(msg);
  };
  const [history, setHistory] = useState<string[]>([]);
  const historyRef = useRef<string[]>([]);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isConciergeActive, setIsConciergeActive] = useState(initialConcierge);
  const [audioPlaybackFailed, setAudioPlaybackFailed] = useState(false);
  const [availableVoices] = useState([
    { name: "Charon (Deep - Male Default)", voiceURI: "Charon", lang: "en-GB" },
    { name: "Kore (Calm - Female)", voiceURI: "Kore", lang: "en-GB" },
    { name: "Aoede (Clear - Female)", voiceURI: "Aoede", lang: "en-GB" },
    { name: "Zephyr (Warm - Male)", voiceURI: "Zephyr", lang: "en-GB" },
    { name: "Puck (Playful - Male)", voiceURI: "Puck", lang: "en-GB" },
    { name: "Fenrir (Intense - Male)", voiceURI: "Fenrir", lang: "en-GB" },
  ]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState<string>("Kore");
  const [playbackRate, setPlaybackRate] = useState<number>(1.0);

  const recognitionRef = useRef<any>(null);
  const isRecognitionActiveRef = useRef(false);

  // Disable background scrolling on mount with dynamic padding-right offset to prevent page layout jumping
  useEffect(() => {
    setMounted(true);
    const originalBodyOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    // Inside iframe preview contexts (window.self !== window.top), modifying body overflow hidden causes parent snap scrolling glitches.
    // Therefore, we bypass body overflow changes when in an iframe.
    const isInsideIframe = typeof window !== "undefined" && window.self !== window.top;

    if (!isInsideIframe) {
      // Only lock and adjust padding if the body is actually scrollable to prevent horizontal screen jumping
      const isScrollable = document.body.scrollHeight > window.innerHeight;
      if (isScrollable) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
        document.body.style.overflow = "hidden";
      }
    }

    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      // Prime SpeechSynthesis voice loading
      window.speechSynthesis.getVoices();
      const handleVoicesChanged = () => {
        window.speechSynthesis.getVoices();
      };
      window.speechSynthesis.addEventListener("voiceschanged", handleVoicesChanged);
      return () => {
        window.speechSynthesis.removeEventListener("voiceschanged", handleVoicesChanged);
        if (!isInsideIframe) {
          document.body.style.overflow = originalBodyOverflow;
          document.body.style.paddingRight = originalPaddingRight;
        }
      };
    }

    return () => {
      if (!isInsideIframe) {
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      }
    };
  }, []);

  const safeStartRecognition = () => {
    if (!recognitionRef.current) return;
    if (isRecognitionActiveRef.current) {
      console.log("[SPEECH RECOGNITION] Already active, skipping start call.");
      return;
    }
    try {
      isRecognitionActiveRef.current = true;
      recognitionRef.current.start();
    } catch (e: any) {
      console.warn("Failed to start speech recognition gracefully:", e);
      if (e?.message && e.message.includes("already started")) {
        isRecognitionActiveRef.current = true;
      } else {
        isRecognitionActiveRef.current = false;
      }
    }
  };

  const safeStopRecognition = () => {
    if (!recognitionRef.current) return;
    if (!isRecognitionActiveRef.current) {
      console.log("[SPEECH RECOGNITION] Already inactive, skipping stop call.");
      return;
    }
    try {
      recognitionRef.current.stop();
    } catch (e) {
      console.warn("Failed to stop speech recognition gracefully:", e);
    }
  };

  // Visualizer Refs for User
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number | null>(null);
  
  // Visualizer Refs for AI
  const aiCanvasRef = useRef<HTMLCanvasElement>(null);
  const aiAnalyserRef = useRef<AnalyserNode | null>(null);
  const aiAnimationRef = useRef<number | null>(null);
  const aiSourceRef = useRef<AudioBufferSourceNode | null>(null);

  useEffect(() => {
    if (isListening) {
      startVisualizer();
    } else {
      stopVisualizer();
    }
  }, [isListening]);

  const startVisualizer = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      streamRef.current = stream;

      if (!audioContextRef.current || audioContextRef.current.state === "closed") {
         const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
         audioContextRef.current = new AudioCtx();
      }
      
      const audioContext = audioContextRef.current;
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64; // Small size for fewer bars
      analyserRef.current = analyser;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const canvasCtx = canvas.getContext("2d");
      if (!canvasCtx) return;

      const draw = () => {
        animationRef.current = requestAnimationFrame(draw);
        analyser.getByteFrequencyData(dataArray);

        canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

        // Calculate bar width relative to canvas size
        const barWidth = (canvas.width / bufferLength) * 1.5;
        let x = 0;

        for (let i = 0; i < bufferLength; i++) {
          // Soften the height
          const barHeight = isMutedRef.current
            ? 2
            : Math.max(2, (dataArray[i] / 255) * canvas.height);

          canvasCtx.fillStyle = isMutedRef.current ? "#ef4444" : "#6366f1"; // red-500 or indigo-500
          canvasCtx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);

          x += barWidth + 2;
        }
      };

      draw();
    } catch (e) {
      console.warn("Visualizer microphone access denied or not available", e);
    }
  };

  const stopVisualizer = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const simulationAnimationRef = useRef<number | null>(null);

  const startSimulatedAiVisualizer = () => {
    const canvas = aiCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    let frame = 0;
    const barCount = 32;

    const drawSimulation = () => {
      simulationAnimationRef.current = requestAnimationFrame(drawSimulation);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      frame++;
      const barWidth = (canvas.width / barCount) * 1.5;
      let x = 0;

      for (let i = 0; i < barCount; i++) {
        // Create realistic acoustic frequency spikes with dynamic sine-wave interpolation
        const baseOffset = Math.sin(frame * 0.15 + i * 0.3) * Math.cos(frame * 0.08 + i * 0.1);
        const randFluc = Math.random() * 0.3 + 0.75;
        const amplitude = Math.max(2, (Math.abs(baseOffset) * randFluc * canvas.height * 0.9));
        
        ctx.fillStyle = `rgb(0, 240, 255)`; // Elegant NexoraX cyan
        ctx.fillRect(x, canvas.height - amplitude, barWidth, amplitude);
        x += barWidth + 2;
      }
    };
    
    if (simulationAnimationRef.current) {
      cancelAnimationFrame(simulationAnimationRef.current);
    }
    drawSimulation();
  };

  const stopSimulatedAiVisualizer = () => {
    if (simulationAnimationRef.current) {
      cancelAnimationFrame(simulationAnimationRef.current);
      simulationAnimationRef.current = null;
    }
    const canvas = aiCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const stopAiVisualizer = () => {
    if (aiAnimationRef.current) {
      cancelAnimationFrame(aiAnimationRef.current);
    }
    stopSimulatedAiVisualizer();
    if (aiSourceRef.current) {
      try {
        aiSourceRef.current.stop();
        aiSourceRef.current.disconnect();
      } catch (e) {}
    }
    const canvas = aiCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const speakWithBrowserTTS = (textStr: string, voiceName: string, rate: number, onEnded: () => void) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      onEnded();
      return;
    }
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(textStr);
    utterance.rate = rate;

    const voices = window.speechSynthesis.getVoices();
    const enVoices = voices.filter(v => v.lang.toLowerCase().startsWith('en'));
    const lowerName = (voiceName || "").toLowerCase();
    let targetVoice = null;
    
    // Prioritized score-based match for maximum natural sound quality
    if (lowerName.includes("puck")) {
      targetVoice = enVoices.find(v => v.name.toLowerCase().includes('google us english male')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('puck')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('male')) ||
                    enVoices[0];
    } else if (lowerName.includes("charon") || lowerName.includes("deep") || lowerName.includes("male") || lowerName.includes("zephyr")) {
      targetVoice = enVoices.find(v => v.name.toLowerCase().includes('google uk english male')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('google us english male')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('daniel')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('george')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('david')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('male')) ||
                    enVoices[0];
    } else if (lowerName.includes("kore") || lowerName.includes("aoede") || lowerName.includes("calm") || lowerName.includes("female") || !voiceName) {
      targetVoice = enVoices.find(v => v.name.toLowerCase().includes('google uk english female')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('google us english female')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('samantha')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('siri')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('hazel')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('karen')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('zira')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('female')) ||
                    enVoices[0];
    } else if (lowerName.includes("fenrir")) {
      targetVoice = enVoices.find(v => v.name.toLowerCase().includes('google uk english male')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('daniel')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('male')) ||
                    enVoices[0];
    }
    
    if (!targetVoice) {
      // General fallbacks based on quality
      targetVoice = enVoices.find(v => v.name.toLowerCase().includes('google uk english female')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('google us english female')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('samantha')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('daniel')) ||
                    enVoices.find(v => v.name.toLowerCase().includes('david')) ||
                    voices.find(v => v.lang.toLowerCase().startsWith('en')) ||
                    voices[0];
    }

    if (targetVoice) {
      utterance.voice = targetVoice;
    }

    utterance.onstart = () => {
      startSimulatedAiVisualizer();
    };

    utterance.onend = () => {
      stopSimulatedAiVisualizer();
      onEnded();
    };

    utterance.onerror = (e) => {
      console.warn("SpeechSynthesis error:", e);
      stopSimulatedAiVisualizer();
      onEnded();
    };

    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setResponse("[SYSTEM] Web Speech API is not supported in this browser. Please use Google Chrome, Safari, or Microsoft Edge for real-time voice link capabilities, or use the Command entry console to type your directives.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-GB";

    recognition.onstart = () => {
      isRecognitionActiveRef.current = true;
      setIsListening(true);
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };

    recognition.onresult = (event: any) => {
      let currentTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        currentTranscript += event.results[i][0].transcript;
      }
      if (!isMutedRef.current) {
        setTranscript(currentTranscript);
        transcriptRef.current = currentTranscript;
      }
    };

    recognition.onend = () => {
      isRecognitionActiveRef.current = false;
      const finalTranscript = transcriptRef.current;
      if (!isMutedRef.current && finalTranscript.trim().length > 0) {
        handleUserMessage(finalTranscript.trim());
      } else {
        // If empty transcript and shouldBeListeningRef is true (no speech timed out), auto-restart
        if (shouldBeListeningRef.current) {
          setTimeout(() => {
            if (shouldBeListeningRef.current) {
              safeStartRecognition();
            }
          }, 150);
        }
      }
      
      // Stabilize isListening - only toggle off if we are not expecting continuous auto-reconnection
      if (!shouldBeListeningRef.current) {
        setIsListening(false);
      }
      transcriptRef.current = "";
    };

    recognition.onerror = (event: any) => {
      isRecognitionActiveRef.current = false;
      
      if (event.error === "no-speech" || event.error === "aborted") {
        console.warn("[SPEECH RECOGNITION WARN] Type:", event.error);
      } else {
        console.error("[SPEECH RECOGNITION ERROR] Type:", event.error);
      }
      
      let descriptiveText = "";
      if (event.error === "not-allowed") {
        shouldBeListeningRef.current = false;
        setIsListening(false);
        descriptiveText = "Microphone access blocked. If you are on the embedded preview, open the app in a new tab via the icon in the top-right of your screen to grant direct microphone permissions to this domain.";
        setResponse(`[MIC HANDSHAKE EXCEPTION] ${descriptiveText}`);
      } else if (event.error === "no-speech") {
        // Silent timeout. Do not overwrite response or toggle isListening false so UX stays fully stable.
        console.log("[SPEECH RECOGNITION] Silence detected. Restarting continuous stream standard.");
      } else if (event.error === "aborted") {
        // Manual stop. Only set listening false if intentional toggle occurred
        if (!shouldBeListeningRef.current) {
          setIsListening(false);
        }
        console.log("[SPEECH RECOGNITION] Aborted manually.");
      } else if (event.error === "service-not-allowed") {
        shouldBeListeningRef.current = false;
        setIsListening(false);
        descriptiveText = "The Speech Recognition service is not permitted inside this sandboxed layout. Open the app in a new tab.";
        setResponse(`[MIC HANDSHAKE EXCEPTION] ${descriptiveText}`);
      } else if (event.error === "audio-capture") {
        shouldBeListeningRef.current = false;
        setIsListening(false);
        descriptiveText = "No microphone hardware was detected or capture failed.";
        setResponse(`[MIC HANDSHAKE EXCEPTION] ${descriptiveText}`);
      } else {
        shouldBeListeningRef.current = false;
        setIsListening(false);
        descriptiveText = `Link disruption: ${event.error}`;
        setResponse(`[MIC HANDSHAKE EXCEPTION] ${descriptiveText}`);
      }
    };

    return () => {
      shouldBeListeningRef.current = false;
      try {
        recognition.stop();
      } catch (err) {}
      isRecognitionActiveRef.current = false;

      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      
      // Memory Leak Prevention: Ensure visualizers and audio context are cleanly disposed
      stopVisualizer();
      stopAiVisualizer();
      
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch(() => {});
        aiAnalyserRef.current = null;
        analyserRef.current = null;
      }
    };
  }, []);

  const startConnection = async () => {
    // Prime SpeechSynthesis synchronously on user click to unlock future asynchronous playbacks
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
        const silentUtterance = new SpeechSynthesisUtterance(" ");
        silentUtterance.volume = 0;
        window.speechSynthesis.speak(silentUtterance);
      } catch (e) {
        console.warn("SpeechSynthesis prime bypassed inside modal", e);
      }
    }

    setIsAuthorized(true);

    // Verify audio container support before attempting connection to prevent 'Unable to decode audio data' errors
    const audioTest = document.createElement("audio");
    const supportsAudio = 
      audioTest.canPlayType("audio/mp3") || 
      audioTest.canPlayType("audio/wav") || 
      audioTest.canPlayType("audio/webm") || 
      audioTest.canPlayType("audio/ogg") ||
      audioTest.canPlayType("audio/mp4");

    if (!supportsAudio) {
      console.warn("Unable to decode audio data: Container formats not supported.");
      setAudioPlaybackFailed(true);
      // We can continue but flag that playback failed so user knows
    }

    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!audioContextRef.current || audioContextRef.current.state === "closed") {
       audioContextRef.current = new AudioCtx();
       aiAnalyserRef.current = null;
       analyserRef.current = null;
    }
    // Resume to handle strict browser autoplay policies
    if (audioContextRef.current.state === "suspended") {
      try {
        await audioContextRef.current.resume();
      } catch (err) {
        setAudioPlaybackFailed(true);
      }
    }

    setResponse("[SYSTEM] Initializing handshake...");
    try {
      const res = await fetch("/api/voice/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: isConciergeActive 
            ? "Hello. Please greet me warmly as the NexoraX Concierge. Praise be to Allah. Welcome me and ask how you can make my technical or placement tasks beautiful today."
            : "Hello. Please greet me as Client, give me a brief 1-sentence overview of the NexoraX Digital OS website, and ask for my name.",
          history: historyRef.current,
          mode: isConciergeActive ? "concierge" : "standard"
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setResponse(data.text);
        const newHistory = [
          "Hello. Please greet me as Client, give me a brief 1-sentence overview of the NexoraX Digital OS website, and ask for my name.",
          data.text,
        ];
        setHistory(newHistory);
        historyRef.current = newHistory;
        speakText(data.text);
      }
    } catch (e) {
      setResponse("[ERROR] Handshake failed.");
    }
  };

  // Dedicated registry mapping voice command intent strings to specific global actions / layouts.
  const VOICE_INTENT_COMMANDS = [
    {
      actionName: "open terminal",
      intents: ["open terminal", "launch terminal", "activate terminal", "go to terminal", "open architect terminal", "show terminal", "terminal node", "vanguard terminal", "architect terminal"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: null,
      text: "Absolute authority. Navigating directly to the primary Node 0 Architect Command Terminal."
    },
    {
      actionName: "start audit",
      intents: ["start audit", "begin audit", "run audit", "activate audit", "open audit", "launch audit", "verify audit", "audit ledger", "reality audit"],
      mainTab: "REALITY AUDIT LEDGER",
      landingTab: null,
      text: "Mandate activated. Displaying physical reality audit terminal and multi-stage corporate tax extraction ledger."
    },
    {
      actionName: "open site editor",
      intents: ["open site editor", "start site editor", "go to site editor", "website site editor", "site editor", "edit website"],
      mainTab: "WEBSITE SITE EDITOR",
      landingTab: null,
      text: "Confirmed. Accessing the proprietary NexoraX Site Content Editor."
    },
    {
      actionName: "open crm",
      intents: ["open crm", "crm dashboard", "slide deck", "check client metrics", "client metrics", "show crm"],
      mainTab: "CRM, DASHBOARD & SLIDE DECK",
      landingTab: null,
      text: "Understood. Deploying client CRM metrics dashboard and investment slide pitch."
    },
    {
      actionName: "open drone swarm",
      intents: ["open drone swarm", "start drone swarm", "view drone swarm", "go to drone swarm", "drone swarm", "drone module", "spectacle", "spectacle command"],
      mainTab: "SPECTACLE COMMAND",
      landingTab: null,
      text: "Affirmative. Routing tactical deployment grid to Node 09: Autonomous Drone Swarms and Stadium Spectacles."
    },
    {
      actionName: "open brand defense",
      intents: ["open branding", "view brand protection", "brand protection", "branding command", "brand defense", "reputation", "cognitive defense"],
      mainTab: "BRANDING COMMAND",
      landingTab: null,
      text: "Roger that. Displaying active Brand protection intelligence and protection command."
    },
    {
      actionName: "open secretary node",
      intents: ["open secretary", "secretary node", "chief of staff", "secretary console"],
      mainTab: "SECRETARY NODE",
      landingTab: null,
      text: "Affirmative. Opening Chief of Staff administrative console and Secretary task queues."
    },
    {
      actionName: "open threat evaluation",
      intents: ["threat evaluation", "hostile intercept", "security panel", "rogue", "extermination", "threat alert", "security alert"],
      mainTab: "ROGUE ENTITY ELIMINATION",
      landingTab: null,
      text: "High alert. Navigating to Rogue Entity and Hostile Intercept grid array."
    },
    {
      actionName: "open dynamic payload",
      intents: ["payload matrix", "dynamic payload", "open payload matrix", "payload config"],
      mainTab: "DYNAMIC PAYLOAD MATRIX",
      landingTab: null,
      text: "Acknowledged. Deploying Dynamic JSON Payload Matrix configuration deck."
    },
    {
      actionName: "open vault",
      intents: ["open vault", "activate vault", "unlock vault", "access vault", "NexoraX Resource Library"],
      mainTab: "REALITY AUDIT LEDGER",
      landingTab: null,
      text: "NexoraX Resource Library level cleared. Mirroring current background source synchronizations."
    },
    {
      actionName: "scan infrastructure",
      intents: ["scan infrastructure", "scan nodes", "check infrastructure", "infrastructure integrity"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: "Home",
      text: "Scanning active sector grids. Node 01, 06, 16, and 17 return healthy telemetry indices."
    },
    {
      actionName: "generate report",
      intents: ["generate report", "create revenue report", "build ledger report", "compile harvest"],
      mainTab: "REALITY AUDIT LEDGER",
      landingTab: null,
      text: "Financial extraction report compiled. Net settlements routed to trust ledger sequence."
    },
    {
      actionName: "system status",
      intents: ["system status", "check nodes", "how is nexorax doing", "heartbeat state"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: "Home",
      text: "NexoraX Core OS reporting perfect execution velocity at 0.8 seconds latency."
    },
    {
      actionName: "knowledge base",
      intents: ["knowledge base", "open documentation", "access knowledge", "architect knowledge"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: null,
      text: "Deploying NexoraX Knowledge Base panel for technical systems architectures."
    },
    {
      actionName: "customer support",
      intents: ["customer support", "support channel", "contact support", "administrative support"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: null,
      text: "Directing technical support queries to our Enterprise Mail Core at support@nexoraxdigital.co.uk and the 08000 freephone frequency."
    },
    {
      actionName: "inbound Q&A",
      intents: ["inbound q and a", "ask questions", "faq node", "frequently asked questions"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: null,
      text: "Predictive Q&A responder online. Awaiting inbound information request."
    },
    {
      actionName: "inbound & outbound support",
      intents: ["inbound and outbound support", "full support routing", "unified support link"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: null,
      text: "Initiating Pakistan Node Synchronization. General support lines synchronized."
    },
    {
      actionName: "help on login & join",
      intents: ["help on login and join", "sign up help", "authentication help", "mfa support", "how to join"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: "Login",
      text: "Activating step-by-step guidance for authentication credentialing. Switch landing to login node."
    },
    {
      actionName: "show help",
      intents: ["show help", "help", "open help", "launch help", "activate help", "hotkey help", "help modal", "voice command help", "voice help"],
      mainTab: null,
      landingTab: null,
      action: "SHOW_HELP",
      text: "Deploying hotkeys, operational shortcuts, and NexoraX voice command documentation panel."
    },
    {
      actionName: "lock terminal",
      intents: ["lock terminal", "lock screen", "lock session", "disconnect session", "terminate session", "lock node", "exit session", "logout", "secure screen"],
      mainTab: null,
      landingTab: null,
      action: "LOCK_TERMINAL",
      text: "Understood. Securing NexoraX grid. Severing active OAuth and terminating terminal session immediately."
    },
    {
      actionName: "navigate to home",
      intents: ["navigate to home", "go to home", "return to home", "go home", "home", "back to home", "vanguard home", "vanguard dashboard"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: "Home",
      text: "Acknowledged. Activating Vanguard Home landing matrix and dashboard."
    },
    {
      actionName: "welcome message",
      intents: ["welcome message", "welcome", "greetings", "hello", "hi architect", "initialize handshake"],
      mainTab: "NODE 0: ARCHITECT TERMINAL",
      landingTab: "Home",
      text: "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. Welcome, NexoraX User. This is the NexoraX Digital Master Operating System. Core node structures, financial extraction ledgers, and drone systems are active at 0.8s latency."
    }
  ];

  const parseVoiceNavigation = (spokenText: string) => {
    const t = spokenText.toLowerCase().trim();

    // Check Voice Command Intent Registry first for direct mapping
    for (const cmd of VOICE_INTENT_COMMANDS) {
      if (cmd.intents.some(intent => t === intent || t.includes(intent))) {
        return {
          mainTab: cmd.mainTab,
          landingTab: cmd.landingTab || null,
          text: cmd.text,
          action: (cmd as any).action || null
        };
      }
    }
    
    // Concierge-specific task routing
    if (isConciergeActive) {
      if (t.includes("settlement") || t.includes("bank") || t.includes("SecurePayment") || t.includes("gavel")) {
        return {
          mainTab: "REALITY AUDIT LEDGER",
          landingTab: null,
          text: "Protocol acknowledged. Accessing Chronos Ledger to review Secure Payment Provider settlements and the active tiered corporate tax extraction ledger."
        };
      }
      if (t.includes("placement") || t.includes("Secure Invoice Register") || t.includes("candidate") || t.includes("recruit")) {
        return {
          mainTab: "NODE 0: ARCHITECT TERMINAL",
          landingTab: "NexoraX Talent Hub",
          text: "Initiating elite talent acquisition routing. Direct-billing to NexoraX billing records under NI reference NH 44 87 96 B has been initiated."
        };
      }
      if (t.includes("migration") || t.includes("compliance") || t.includes("Staff Coordinator")) {
        return {
          mainTab: "SECRETARY NODE",
          landingTab: null,
          text: "Opening Chief of Staff secretary console to audit pending enterprise standards, compliance, and Pakistan Node Sync status lists."
        };
      }
    }
    
    if (t.includes("drone") || t.includes("swarm") || t.includes("spectacle")) {
      return {
        mainTab: "SPECTACLE COMMAND",
        landingTab: null,
        text: "Affirmative. Routing tactical deployment grid to Node 09: Autonomous Drone Swarms and Stadium Spectacles."
      };
    }
    if (t.includes("payload") || t.includes("matrix")) {
      return {
        mainTab: "DYNAMIC PAYLOAD MATRIX",
        landingTab: null,
        text: "Acknowledged. Deploying Dynamic JSON Payload Matrix configuration deck."
      };
    }
    if (t.includes("site editor") || (t.includes("editor") && t.includes("site"))) {
      return {
        mainTab: "WEBSITE SITE EDITOR",
        landingTab: null,
        text: "Confirmed. Accessing the proprietary Nexorax Site Content Editor."
      };
    }
    if (t.includes("branding") || t.includes("brand protection") || t.includes("reputation")) {
      return {
        mainTab: "BRANDING COMMAND",
        landingTab: null,
        text: "Roger that. Displaying active Brand protection intelligence and protection command."
      };
    }
    if (t.includes("crm") || t.includes("dashboard") || t.includes("slide deck")) {
      return {
        mainTab: "CRM, DASHBOARD & SLIDE DECK",
        landingTab: null,
        text: "Understood. Accessing client metrics, CRM dashboard, and investment slide pitch."
      };
    }
    if (t.includes("secretary") || t.includes("chief of staff")) {
      return {
        mainTab: "SECRETARY NODE",
        landingTab: null,
        text: "Affirmative. Opening Chief of Staff administrative console and Secretary task queues."
      };
    }
    if (t.includes("reality audit") || t.includes("audit ledger")) {
      return {
        mainTab: "REALITY AUDIT LEDGER",
        landingTab: null,
        text: "Mandated verification active. Displaying physical reality audit terminal."
      };
    }
    if (t.includes("rogue") || t.includes("extermination") || t.includes("threat") || t.includes("hostile") || t.includes("security")) {
      return {
        mainTab: "ROGUE ENTITY ELIMINATION",
        landingTab: null,
        text: "High alert. Navigating to Rogue Entity and Hostile Intercept grid array."
      };
    }
    if (t.includes("architect terminal") || t.includes("node 0") || t.includes("main console")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: null,
        text: "Returning to Node 0 Primary Architect command registry."
      };
    }
    if (t.includes("home") || t.includes("go back to home") || t === "go home") {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "Home",
        text: "Acknowledged. Activating Vanguard Home landing matrix."
      };
    }
    if (t.includes("team") || t.includes("personnel") || t.includes("pakistan node")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "Team",
        text: "Connecting communications pipeline to global personnel and Pakistan Node Sync."
      };
    }
    if (t.includes("tier") || t.includes("pricing") || t.includes("membership")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "Tiers",
        text: "Displaying NexoraX expansion investment tier matrices."
      };
    }
    if (t.includes("charit") || t.includes("levy") || t.includes("3% Community Allocation")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "Charities",
        text: "Accessing the 3% Community Allocation allocations log (Charities 1, 2, and 3)."
      };
    }
    if (t.includes("proposal automator") || t.includes("proposal compiler")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "Proposal Automator",
        text: "Initializing Node-21 Automated Proposal compiler engine."
      };
    }
    if (t.includes("talent") || t.includes("Secure Payment Provider") || t.includes("NexoraX billing records")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "NexoraX Talent Hub",
        text: "Directing focus to NexoraX billing records offset candidate acquisitions."
      };
    }
    if (t.includes("incubator") || t.includes("category 91")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "NexoraX Incubator",
        text: "Opening Category 91 NexoraX Incubator multi-module console."
      };
    }
    if (t.includes("master engine") || t.includes("unified matrix")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "NexoraX Master Engine",
        text: "Deploying high density NexoraX Master Engine layout."
      };
    }
    if (t.includes("hermes") || t.includes("outreach")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "NexoraX Hermes Node",
        text: "Accessing Hermes neural outreach simulator dashboards."
      };
    }
    if (t.includes("marketplace") || t.includes("store")) {
      return {
        mainTab: "NODE 0: ARCHITECT TERMINAL",
        landingTab: "NexoraX Marketplace",
        text: "Launching luxury checkout portal and node settlement system."
      };
    }
    return null;
  };

  const handleUserMessage = async (text: string) => {
    const t = text.toLowerCase().trim();

    // Command patterns for mode toggle
    if (t.includes("concierge node") || t.includes("concierge mode") || t.includes("activate concierge") || t.includes("NexoraX concierge")) {
      setIsConciergeActive(true);
      const welcomeText = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds. Welcome to the NexoraX Sanctum. I'm your NexoraX Concierge Node. Ready to guide your elite corporate routing, subsidies, and global settlements.";
      setResponse(welcomeText);
      const newHistory = [
        text,
        welcomeText,
      ];
      setHistory(newHistory);
      historyRef.current = newHistory;
      speakText(welcomeText);
      return;
    }

    if (t.includes("standard mode") || t.includes("architect mode") || t.includes("deactivate concierge")) {
      setIsConciergeActive(false);
      const standardText = "[SYSTEM] Standard Architect Mode selected. Handshake protocols fully restored.";
      setResponse(standardText);
      const newHistory = [
        text,
        standardText,
      ];
      setHistory(newHistory);
      historyRef.current = newHistory;
      speakText(standardText);
      return;
    }

    const navResult = parseVoiceNavigation(text);
    if (navResult) {
      setResponse(navResult.text);
      const newHistory = [...historyRef.current, text, navResult.text];
      setHistory(newHistory);
      historyRef.current = newHistory;
      speakText(navResult.text);

      if (navResult.action) {
        const actionEvent = new CustomEvent("nexorax-voice-action", {
          detail: {
            action: navResult.action
          }
        });
        window.dispatchEvent(actionEvent);
      }

      if (navResult.mainTab !== null && navResult.mainTab !== undefined) {
        const event = new CustomEvent("nexorax-voice-navigate", {
          detail: {
            mainTab: navResult.mainTab,
            landingTab: navResult.landingTab
          }
        });
        window.dispatchEvent(event);
      }
      return;
    }

    setResponse("[SYSTEM] Analyzing directive...");
    shouldBeListeningRef.current = false;
    try {
      const res = await fetch("/api/voice/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          text, 
          history: historyRef.current,
          mode: isConciergeActive ? "concierge" : "standard"
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setResponse(data.text);
        const newHistory = [...historyRef.current, text, data.text];
        setHistory(newHistory);
        historyRef.current = newHistory;
        speakText(data.text);
      } else {
        setResponse(`[ERROR] ${data.message}`);
        speakText("I encountered an internal error. Please try again.");
      }
    } catch (e: any) {
      setResponse(`[ERROR] Failed to connect: ${e.message}`);
      speakText("Network connection to the neural core is unstable.");
    }
  };

   const speakText = async (text: string) => {
    setIsAiSpeaking(true);
    stopAiVisualizer();
    try {
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      const res = await fetch("/api/generate-voice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, voiceName: selectedVoiceURI })
      });
      const data = await res.json();
      if (data.status === "success" && data.audio) {
        await playAudioBase64AndVisualize(data.audio, data.mimeType);
      } else {
        console.warn("Real TTS synthesis fell back. Employing client-side Google/NexoraX Voice Synthesis.");
        speakWithBrowserTTS(text, selectedVoiceURI, playbackRate, () => {
          setIsAiSpeaking(false);
          shouldBeListeningRef.current = true;
          safeStartRecognition();
        });
      }
    } catch (e) {
      console.warn("Caught TTS fetch exception. Falling back to local browser SpeechSynthesis matrix:", e);
      speakWithBrowserTTS(text, selectedVoiceURI, playbackRate, () => {
        setIsAiSpeaking(false);
        shouldBeListeningRef.current = true;
        safeStartRecognition();
      });
    }
  };

  const playAudioBase64AndVisualize = async (base64Audio: string, mimeType?: string) => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!audioContextRef.current || audioContextRef.current.state === "closed") {
         audioContextRef.current = new AudioCtx();
         aiAnalyserRef.current = null;
         analyserRef.current = null;
      }
      
      const audioCtx = audioContextRef.current;
      if (audioCtx.state === "suspended") {
        try {
          await audioCtx.resume();
        } catch (resErr) {
          console.warn("Could not resume AudioContext:", resErr);
        }
      }
      
      const binaryString = window.atob(base64Audio);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      let audioBuffer: AudioBuffer | null = null;
      
      const isRawPCM = mimeType && (mimeType.includes("l16") || mimeType.includes("pcm"));
      
      if (!isRawPCM) {
        try {
          audioBuffer = await audioCtx.decodeAudioData(bytes.buffer.slice(0));
        } catch (decodeErr) {
          // fallback later
        }
      }
      
      if (!audioBuffer) {
        // Safe alignment-independent fallback for raw PCM 16-bit
        let sampleRate = 24000;
        if (mimeType) {
          const rateMatch = mimeType.match(/rate=(\d+)/);
          if (rateMatch) {
            sampleRate = parseInt(rateMatch[1], 10);
          }
        }
        const sampleCount = Math.floor(bytes.byteLength / 2);
        const pcm16 = new Int16Array(sampleCount);
        const dataView = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        for (let i = 0; i < sampleCount; i++) {
          pcm16[i] = dataView.getInt16(i * 2, true); // true for little-endian
        }
        audioBuffer = audioCtx.createBuffer(1, sampleCount, sampleRate);
        const channelData = audioBuffer.getChannelData(0);
        for (let i = 0; i < sampleCount; i++) {
          channelData[i] = pcm16[i] / 32768.0;
        }
      }

      if (aiSourceRef.current) {
        try {
          aiSourceRef.current.stop();
          aiSourceRef.current.disconnect();
        } catch (e) {}
      }

      const source = audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.playbackRate.value = playbackRate;
      aiSourceRef.current = source;
      
      if (aiAnalyserRef.current && (aiAnalyserRef.current.context as any) !== audioCtx) {
        aiAnalyserRef.current = null;
      }
      
      if (!aiAnalyserRef.current) {
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 128;
        aiAnalyserRef.current = analyser;
      }
      
      source.connect(aiAnalyserRef.current);
      aiAnalyserRef.current.connect(audioCtx.destination);
      
      source.onended = () => {
        setIsAiSpeaking(false);
        stopAiVisualizer();
        // Auto-resume listening
        if (recognitionRef.current) {
          setTranscript("");
          transcriptRef.current = "";
          shouldBeListeningRef.current = true;
          safeStartRecognition();
        }
      };
      
      source.start(0);
      drawAiVisualizer();
      
    } catch (e) {
      console.error("[Visualizer Error]", e);
      setIsAiSpeaking(false);
    }
  };

  const drawAiVisualizer = () => {
    const canvas = aiCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const analyser = aiAnalyserRef.current;
    if (!analyser) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      aiAnimationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = Math.max(2, (dataArray[i] / 255) * canvas.height);
        ctx.fillStyle = `rgb(0, 240, 255)`; // Cyan color matching AI theme
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        x += barWidth + 2;
      }
    };
    draw();
  };

  const toggleListening = async () => {
    // Prime the audio contexts on direct user action
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!audioContextRef.current || audioContextRef.current.state === "closed") {
      audioContextRef.current = new AudioCtx();
    }
    if (audioContextRef.current.state === "suspended") {
      try {
        await audioContextRef.current.resume();
      } catch (err) {
        console.warn("Could not resume AudioContext:", err);
      }
    }

    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
        const silentUtterance = new SpeechSynthesisUtterance(" ");
        silentUtterance.volume = 0;
        window.speechSynthesis.speak(silentUtterance);
      } catch (e) {
        console.warn("SpeechSynthesis lock-prime bypassed during toggle", e);
      }
    }

    if (isListening) {
      shouldBeListeningRef.current = false;
      safeStopRecognition();
    } else {
      setTranscript("");
      transcriptRef.current = "";
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      shouldBeListeningRef.current = true;
      safeStartRecognition();
    }
  };

  if (!mounted || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div 
      id="architect-voice-modal-overlay"
      className="fixed inset-0 z-[2147483647] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl overflow-y-auto isolate selection:bg-indigo-500/30 selection:text-indigo-50"
      style={{ isolation: "isolate" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`bg-[#050914] border-2 rounded-2xl w-full max-w-2xl flex flex-col overflow-hidden relative transition-all duration-500 ${isAiSpeaking ? "border-[#00f0ff] shadow-[0_0_40px_-5px_rgba(0,240,255,0.4)] animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]" : "border-indigo-500/50 shadow-[0_0_50px_-10px_rgba(99,102,241,0.6)]"}`}
      >
        <div
          className={`absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] ${isAiSpeaking ? "from-[#00f0ff]/10 via-transparent to-transparent" : "from-indigo-900/20 via-transparent to-transparent"} pointer-events-none transition-colors duration-500`}
        ></div>

        <div
          className={`flex items-start justify-between p-6 border-b transition-colors duration-500 ${isAiSpeaking ? "border-[#00f0ff]/30" : "border-indigo-500/30"}`}
        >
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-3 rounded-full ${isListening ? "bg-red-500 animate-pulse" : "bg-indigo-500"}`}
              ></div>
              <span className="font-mono text-sm font-bold text-indigo-400 uppercase tracking-widest flex items-center gap-2">
                NEXORAX AI VOICE LINK
              </span>
            </div>
            
            <div className="flex items-center gap-2 mt-1">
              <button
                type="button"
                onClick={() => {
                  setIsConciergeActive(false);
                  setResponse("[SYSTEM] Standard Architect Mode selected.");
                  setHistory([]);
                  historyRef.current = [];
                }}
                className={`px-3 py-1 font-mono text-[9px] font-black tracking-wider uppercase rounded transition-all border ${!isConciergeActive ? "bg-indigo-600 border-indigo-500 text-white shadow-[0_0_10px_rgba(99,102,241,0.5)]" : "bg-black/30 border-indigo-900 text-indigo-400"}`}
              >
                Architect Mode
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsConciergeActive(true);
                  setResponse("[SYSTEM] NexoraX Concierge Sanctum selected.");
                  setHistory([]);
                  historyRef.current = [];
                }}
                className={`px-3 py-1 font-mono text-[9px] font-black tracking-wider uppercase rounded transition-all border ${isConciergeActive ? "bg-purple-600 border-purple-500 text-white shadow-[0_0_10px_rgba(168,85,247,0.5)]" : "bg-black/30 border-purple-900 text-purple-400"}`}
              >
                Concierge Sanctum
              </button>
            </div>
            {/* Real-time AI Audio Visualizer */}
            <div className={`mt-1 h-8 overflow-hidden transition-all duration-300 ${isAiSpeaking ? "opacity-100 w-[200px]" : "opacity-0 w-[0px]"}`}>
               <canvas ref={aiCanvasRef} width={200} height={32} className="w-full h-full object-contain object-left" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-indigo-300">SPEED: {playbackRate.toFixed(1)}x</span>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={playbackRate}
                onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                className="w-24 accent-indigo-500"
              />
            </div>
            {availableVoices.length > 0 && (
              <select
                className="bg-indigo-900/40 border border-indigo-500/50 text-indigo-200 font-mono text-xs rounded px-2 py-1.5 outline-none max-w-[200px] truncate"
                value={selectedVoiceURI}
                onChange={(e) => setSelectedVoiceURI(e.target.value)}
              >
                {availableVoices.map((v) => (
                  <option key={v.voiceURI} value={v.voiceURI}>
                    {v.name} ({v.lang})
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors relative z-10 w-8 h-8 flex items-center justify-center rounded hover:bg-white/10"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 flex flex-col relative z-10 w-full h-[600px] max-h-[calc(100vh-140px)] sm:max-h-[80vh]">
          {!isAuthorized ? (
            <div className="flex-1 flex items-center justify-center">
               <button 
                 onClick={startConnection}
                 className="flex items-center gap-3 bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-4 rounded-xl font-mono uppercase tracking-[0.2em] font-bold text-sm shadow-[0_0_20px_rgba(99,102,241,0.5)] transition-all animate-[pulse_3s_infinite]"
               >
                 <Zap className="w-5 h-5 text-indigo-200" />
                 Authorize Neural Link
               </button>
            </div>
          ) : (
            <>
              {/* Scrollable History Log */}
              <div className="flex-1 overflow-y-auto mb-6 space-y-4 pr-3 custom-scrollbar">
            {history.map((msg, i) => {
              if (i === 0 && msg.startsWith("Hello. Please greet me"))
                return null;
              const isUser = i % 2 === 0;
              return (
                <div
                  key={i}
                  className={`flex flex-col ${isUser ? "items-end" : "items-start"} w-full`}
                >
                  <div
                    className={`p-4 rounded-xl max-w-[85%] ${isUser ? "bg-indigo-900/30 border border-indigo-500/40 rounded-br-sm" : "bg-black/70 border border-[#00f0ff]/30 shadow-[0_0_20px_rgba(0,240,255,0.1)] rounded-bl-sm"}`}
                  >
                    <div
                      className={`flex items-center gap-2 mb-2 ${isUser ? "justify-end" : "justify-start"}`}
                    >
                      {!isUser && (
                        <Activity className="w-3 h-3 text-[#00f0ff] animate-pulse" />
                      )}
                      <span
                        className={`font-mono text-[9px] uppercase tracking-widest font-bold ${isUser ? "text-indigo-400" : "text-[#00f0ff]"}`}
                      >
                        {isUser ? "CLIENT DIRECTIVE" : "NEXORAX AI"}
                      </span>
                    </div>
                    <p
                      className={`font-sans text-sm sm:text-base leading-relaxed ${isUser ? "text-indigo-50" : "text-slate-200"}`}
                    >
                      {msg}
                    </p>
                  </div>
                </div>
              );
            })}

            {response === "[SYSTEM] Analyzing directive..." && (
              <div className="flex items-start w-full">
                <div className="p-4 rounded-xl max-w-[85%] bg-black/70 border border-[#00f0ff]/30 shadow-[0_0_20px_rgba(0,240,255,0.1)] rounded-bl-sm">
                  <div className="flex items-center gap-2 mb-2 justify-start">
                    <Activity className="w-3 h-3 text-[#00f0ff] animate-pulse" />
                    <span className="font-mono text-[9px] text-[#00f0ff] uppercase tracking-widest font-bold">
                      NEXORAX AI
                    </span>
                  </div>
                  <p className="font-mono text-xs text-[#00f0ff] flex items-center gap-2">
                    <span className="animate-pulse">
                      ANALYZING DIRECTIVE...
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {audioPlaybackFailed && (
            <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center rounded-2xl">
              <button 
                onClick={async () => {
                  try {
                    setAudioPlaybackFailed(false);
                    if (audioContextRef.current && audioContextRef.current.state === "suspended") {
                      await audioContextRef.current.resume();
                    }
                  } catch (e) {
                    setAudioPlaybackFailed(true);
                  }
                }}
                className="bg-[#00f0ff] hover:bg-cyan-400 text-black font-mono font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-xl flex items-center gap-3 shadow-[0_0_30px_rgba(0,240,255,0.6)] animate-pulse"
              >
                <Zap className="w-5 h-5" /> Initialize Playback
              </button>
            </div>
          )}

          {/* Concierge Node Interactive Commands Cheat-Sheet */}
          {isConciergeActive ? (
            <div className="shrink-0 text-[10px] sm:text-xs font-mono text-purple-300 bg-purple-950/20 border border-purple-500/30 rounded-xl p-3 mb-3 flex flex-col space-y-1 shadow-[0_0_15px_rgba(168,85,247,0.1)]">
               <span className="text-purple-400 font-extrabold uppercase tracking-[0.15em] flex items-center gap-1.5 animate-pulse">
                 <Zap className="w-3.5 h-3.5" /> NEXORAX CONCIERGE NODE: ACTIVE DIRECTIVES
               </span>
               <p className="leading-relaxed text-purple-200">
                 The NexoraX Concierge Sanctum is active. You may utilize voice alerts or the text command line to execute the following administrative actions:
               </p>
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pt-1.5 border-t border-purple-500/20 mt-1">
                 <button 
                   type="button" 
                   onClick={() => handleUserMessage("NexoraX Concierge Mode")} 
                   className="text-left font-bold text-purple-400 hover:text-white transition-colors cursor-pointer"
                 >
                   • <span className="underline">"NexoraX Concierge"</span> (Trigger Greeting)
                 </button>
                 <button 
                   type="button" 
                   onClick={() => handleUserMessage("Show placement fees subsidies")} 
                   className="text-left font-bold text-purple-400 hover:text-white transition-colors cursor-pointer"
                 >
                   • <span className="underline">"Placement Subsidies / Secure Invoice Register"</span> (Route to Talent)
                 </button>
                 <button 
                   type="button" 
                   onClick={() => handleUserMessage("Check Secure Payment Provider settlements status")} 
                   className="text-left font-bold text-purple-400 hover:text-white transition-colors cursor-pointer"
                 >
                   • <span className="underline">"Secure Payment Provider settlements"</span> (Route to Ledger)
                 </button>
                 <button 
                   type="button" 
                   onClick={() => handleUserMessage("Audit Pakistan Node enterprise standards compliance status")} 
                   className="text-left font-bold text-purple-400 hover:text-white transition-colors cursor-pointer"
                 >
                   • <span className="underline">"Compliance migration status"</span> (Route to Secretary)
                 </button>
               </div>
            </div>
          ) : (
            <div className="shrink-0 text-[10px] sm:text-xs font-mono text-zinc-450 bg-indigo-950/5 border border-indigo-500/10 rounded-xl p-3 mb-3 flex flex-col space-y-1">
               <span className="text-indigo-400 font-extrabold uppercase tracking-[0.15em] flex items-center gap-1.5">
                 <Zap className="w-3.5 h-3.5 text-indigo-400" /> ACTIVE ARCHITECT SUGGESTED COMMANDS:
               </span>
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pt-1.5 border-t border-indigo-500/10 mt-1">
                 <button type="button" onClick={() => handleUserMessage("Open terminal")} className="text-left text-cyan-400 font-bold hover:text-white transition-colors cursor-pointer">• <span className="underline">"Open Terminal"</span> (Architect Console)</button>
                 <button type="button" onClick={() => handleUserMessage("Start audit")} className="text-left text-cyan-400 font-bold hover:text-white transition-colors cursor-pointer">• <span className="underline">"Start Audit"</span> (Reality Ledger)</button>
                 <button type="button" onClick={() => handleUserMessage("Navigate to home")} className="text-left text-[#00f0ff] font-bold hover:text-white transition-colors cursor-pointer">• <span className="underline">"Navigate to Home"</span></button>
                 <button type="button" onClick={() => handleUserMessage("Show help")} className="text-left text-[#e840f3] font-bold hover:text-white transition-colors cursor-pointer">• <span className="underline">"Show Help"</span></button>
                 <button type="button" onClick={() => handleUserMessage("Lock terminal")} className="text-left text-red-400 font-bold hover:text-white transition-colors cursor-pointer">• <span className="underline">"Lock Terminal"</span></button>
                 <button type="button" onClick={() => handleUserMessage("Welcome message")} className="text-left text-emerald-400 font-bold hover:text-white transition-colors cursor-pointer">• <span className="underline">"Welcome Message"</span></button>
                 <button type="button" onClick={() => handleUserMessage("Go to site editor")} className="text-left text-indigo-300 hover:text-white transition-colors cursor-pointer">• <span className="underline">"Go to Site Editor"</span></button>
                 <button type="button" onClick={() => handleUserMessage("Check reality audit")} className="text-left text-indigo-300 hover:text-white transition-colors cursor-pointer">• <span className="underline">"Check Reality Audit"</span></button>
                 <button type="button" onClick={() => handleUserMessage("Go to drone swarm module")} className="text-left text-indigo-300 hover:text-white transition-colors cursor-pointer">• <span className="underline">"Go to Drone Swarm"</span></button>
                 <button type="button" onClick={() => handleUserMessage("Show brand protection")} className="text-left text-indigo-300 hover:text-white transition-colors cursor-pointer">• <span className="underline">"Show Brand Protection"</span></button>
               </div>
            </div>
          )}

          {/* Brand Diagnostics Advisory Panel */}
          <div className="shrink-0 text-[10px] sm:text-xs font-mono text-zinc-400 bg-black/50 border border-slate-800 rounded-xl p-3 mb-3 flex flex-col space-y-1">
             <span className="text-cyan-400 font-black uppercase tracking-[0.15em] flex items-center gap-1.5">
               <Activity className="w-3.5 h-3.5 animate-pulse text-[#00f0ff]" /> SYSTEM COGNITIVE DIAGNOSTIC LINK
             </span>
             <p className="leading-relaxed">
               Our AI Voice link now features continuous, auto-resuming listening with silence tolerance. Click the main microphone button to activate continuous listening.
             </p>
             {typeof window !== "undefined" && window.self !== window.top && (
               <p className="leading-relaxed text-amber-400 font-bold border border-amber-500/30 bg-amber-950/20 px-2 py-1 rounded">
                 ⚠️ IFRAME PERMISSION NOTICE: Browsers restrict microphone capture inside the embedded workspace preview. Click the "Open in new window" icon in the top-right corner of your screen to bypass the iframe sandbox and grant mic access!
               </p>
             )}
             <p className="leading-relaxed">
               Fallback: If your hardware is restricted or you are in a loud environment, <strong>use the tactical keyboard entry terminal below</strong> to write your directive, and our voice model will synthesize audio response back to you instantly.
             </p>
          </div>

          {/* High-Fidelity Command entry console fallback */}
          <form onSubmit={handleTextInputSubmit} className="shrink-0 mb-4 flex items-center bg-zinc-950/90 border border-indigo-500/20 hover:border-indigo-500/40 rounded-xl p-2 transition-colors">
             <input 
               type="text"
               value={textInput}
               onChange={(e) => setTextInput(e.target.value)}
               placeholder="Type tactical command or ask a question directly here..."
               className="flex-1 bg-transparent border-0 outline-none px-2 py-1.5 text-xs sm:text-sm font-mono text-cyan-200 placeholder-indigo-950/60 focus:ring-0 focus:outline-none"
             />
             <button
               type="submit"
               className="bg-indigo-600/80 hover:bg-indigo-500 text-white font-mono text-[10px] uppercase font-bold tracking-widest px-4 py-2 rounded-lg transition-all border border-indigo-500/40 shrink-0"
             >
               EXECUTE
             </button>
          </form>

          {/* Bottom Control Section */}
          <div className="shrink-0 flex items-center justify-between bg-black/40 border border-indigo-500/20 p-4 rounded-2xl backdrop-blur-md">
            <div className="flex-1 pr-6 flex flex-col justify-center h-full">
              <div className="text-sm font-sans text-slate-300 w-full truncate mb-1">
                {transcript ||
                  (isListening ? "Listening..." : "Tap microphone to speak")}
              </div>
              <div className="w-full h-4 overflow-hidden flex items-center justify-start opacity-70">
                <canvas
                  ref={canvasRef}
                  width={100}
                  height={16}
                  className={`transition-opacity duration-300 ${isListening ? "opacity-100" : "opacity-0"}`}
                />
              </div>
            </div>

            <div className="relative flex items-center gap-4">
              {isListening && (
                <button
                  onClick={() => {
                    const muted = !isMuted;
                    setIsMuted(muted);
                    isMutedRef.current = muted;
                  }}
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    isMuted
                      ? "bg-red-900/50 border border-red-500 hover:bg-red-800/60 shadow-[0_0_15px_rgba(239,68,68,0.4)]"
                      : "bg-indigo-900/40 border border-indigo-500/50 hover:bg-indigo-800/60"
                  }`}
                  title={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? (
                    <MicOff className="w-5 h-5 text-red-400" />
                  ) : (
                    <Mic className="w-5 h-5 text-indigo-400" />
                  )}
                </button>
              )}
              <div className="relative">
                {isListening && !isMuted && (
                  <div className="absolute -inset-4 bg-indigo-500/20 blur-xl rounded-full animate-pulse pointer-events-none"></div>
                )}
                <button
                  onClick={toggleListening}
                  className={`relative w-20 h-20 shrink-0 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ${isListening && !isMuted ? "bg-indigo-900/80 border-2 border-indigo-400 scale-105 shadow-[0_0_30px_rgba(99,102,241,0.6)]" : isListening && isMuted ? "bg-slate-900 border-2 border-red-500/50" : "bg-black border border-indigo-500/40 hover:border-indigo-400 hover:bg-indigo-900/30 hover:shadow-[0_0_20px_rgba(99,102,241,0.3)]"}`}
                >
                  {isListening && isMuted ? (
                    <MicOff className="w-8 h-8 text-red-500 opacity-60" />
                  ) : (
                    <Mic
                      className={`w-8 h-8 transition-colors ${isListening ? "text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" : "text-indigo-400"}`}
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
